import React, { useMemo, useEffect, useState } from 'react';
import { Grid, TextField, InputAdornment, MenuItem, Typography, Select, styled } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { useNewInvoice } from './newInvoiceProvider';

const RedTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: 'red',
    },
  }));

const NewInvoiceSummaryView = () => {
    const [finalResult, setFinalResult] = useState(0);
    const { input } = useNewInvoice();

    const selectTypes: { id: number, label: string }[] = [
        { id: 1, label: "$" },
        { id: 2, label: "%"}
    ];

    const subTotalWithoutTaxes = useMemo(() => {
        let total = 0;
        input?.invoiceItems?.forEach(item => {
            total += Number(item.amount || 0);
        });
        return total;
    }, [input?.invoiceItems]);

    const discountValue = useMemo(() => {
        if (input?.discountAmount) {
            return Number(input.discountAmount);
        } else if (input?.discountPercentage) {
            return Number(input.discountPercentage * subTotalWithoutTaxes / 100);
        }
        return 0;
    }, [input?.discountAmount, input?.discountPercentage, subTotalWithoutTaxes]);

    const shippingValue = useMemo(() => {
        if (input?.shippingAmount) {
            return Number(input.shippingAmount);
        } else if (input?.shippingPercentage) {
            return Number(input.shippingPercentage * subTotalWithoutTaxes / 100);
        }
        return 0;
    }, [input?.shippingAmount, input?.shippingPercentage, subTotalWithoutTaxes]);

    const otherChargesValue = useMemo(() => {
        if (input?.otherChargesAmount) {
            return Number(input.otherChargesAmount);
        } else if (input?.otherChargesPercentage) {
            return Number(input.otherChargesPercentage * subTotalWithoutTaxes / 100);
        }
        return 0;
    }, [input?.otherChargesAmount, input?.otherChargesPercentage, subTotalWithoutTaxes]);

    const subTotalWithAddedValues = useMemo(() => {
        return subTotalWithoutTaxes - Math.abs(discountValue) + shippingValue + otherChargesValue;
    }, [subTotalWithoutTaxes, discountValue, shippingValue, otherChargesValue]);

    const calculateTaxSummary = () => {
        if (!input?.invoiceItems || input?.invoiceItems?.length === 0) return [];
        const taxGroups = input.invoiceItems.reduce((groups, item) => {
            if (!item.invoiceItemTaxes || item.invoiceItemTaxes.length === 0) return groups;

            const itemAmount = (item.quantity || 0) * (item.rate || 0);

            item.invoiceItemTaxes.forEach(tax => {
                const taxId = tax.taxRateEntity?.id || tax.id;
                const taxRate = tax.taxRate || 0;
                const taxName = tax.taxRateEntity?.name || 'Tax';
                const taxAmount = tax.taxAmount || 0;
                const taxKey = taxId ? `tax-${taxId}` : `${taxName}-${taxRate}`;
                if (!groups[taxKey]) {
                    groups[taxKey] = {
                        id: taxId,
                        rate: taxRate,
                        name: taxName,
                        amount: taxAmount,
                        totalTax: 0,
                        baseAmount: 0
                    };
                }

                groups[taxKey].baseAmount += itemAmount;
                if (taxRate > 0) {
                    groups[taxKey].totalTax += itemAmount * (taxRate / 100);
                } else {
                    groups[taxKey].totalTax += Number(taxAmount);
                }
            });
            return groups;
        }, {});

        return Object.values(taxGroups);
    };

    useEffect(() => {
        let itemsTaxesSum = 0;
        const taxSummary = calculateTaxSummary();
        taxSummary.forEach((tax: any) => {
            itemsTaxesSum += tax.totalTax;
        });

        let invoiceTaxesSum = 0;
        input?.invoiceTaxes?.forEach(tax => {
            if (tax.taxRate > 0) {
                invoiceTaxesSum += tax.taxRate * subTotalWithAddedValues / 100;
            } else {
                invoiceTaxesSum += Number(tax.taxAmount || 0);
            }
        });

        setFinalResult(subTotalWithAddedValues + invoiceTaxesSum + itemsTaxesSum);
    }, [subTotalWithAddedValues, input?.invoiceTaxes, input?.invoiceItems]);

    const getDiscountType = () => {
        return input?.discountAmount ? 1 : 2;
    };

    const getShippingType = () => {
        return input?.shippingAmount ? 1 : 2;
    };

    const getOtherChargesType = () => {
        return input?.otherChargesAmount ? 1 : 2;
    };

    const getDiscountDisplayValue = () => {
        if (input?.discountAmount) return Math.abs(input.discountAmount);
        if (input?.discountPercentage) return Math.abs(input.discountPercentage);
        return 0;
    };

    const getShippingDisplayValue = () => {
        if (input?.shippingAmount) return input.shippingAmount;
        if (input?.shippingPercentage) return input.shippingPercentage;
        return 0;
    };

    const getOtherChargesDisplayValue = () => {
        if (input?.otherChargesAmount) return input.otherChargesAmount;
        if (input?.otherChargesPercentage) return input.otherChargesPercentage;
        return 0;
    };

    return (
        <div className='container' style={{zIndex: -1}}>
            <div className='card-body'>
                <Grid style={{ display: "flex", alignItems: "center"}}>
                    <Grid md={6} className='justify-content-start'>
                        {intl.get('newInvoice.subtotal')}
                    </Grid>
                    <Grid md={6} style={{justifyContent: "end", display: "flex"}}>
                        {subTotalWithoutTaxes.toFixed(2)}
                    </Grid>
                </Grid>
                <Grid container xs={12} style={{marginTop: "15px", display: "flex", alignItems: "center"}}>
                    <Grid item xs={4} md={4}>
                        {intl.get('newInvoice.discount')}
                    </Grid>
                    <Grid item xs={4} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={getDiscountDisplayValue()}
                            disabled={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography
                                            style={{width: '100%', padding: '0px', border: 'none', backgroundColor: 'transparent', color: 'gray', fontSize: '14px'}}
                                        >
                                            {getDiscountType() === 1 ? "$" : "%"}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={4} md={4} style={{justifyContent: "end", display: "flex"}}>
                        -{Math.abs(discountValue).toFixed(2)}
                    </Grid>
                </Grid>

                <Grid container xs={12} style={{marginTop: "15px", display: "flex", alignItems: "center"}}>
                    <Grid item xs={4} md={4}>
                        {intl.get('newInvoice.shipping')}
                    </Grid>
                    <Grid item xs={4} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={getShippingDisplayValue()}
                            disabled={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography
                                            style={{width: '100%', padding: '0px', border: 'none', backgroundColor: 'transparent', color: 'gray', fontSize: '14px'}}
                                        >
                                            {getShippingType() === 1 ? "$" : "%"}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={4} md={4} style={{justifyContent: "end", display: "flex"}}>
                        {shippingValue.toFixed(2)}
                    </Grid>
                </Grid>

                <Grid container xs={12} style={{marginTop: "15px", display: "flex", alignItems: "center"}}>
                    <Grid item xs={4} md={4}>
                        {intl.get('newInvoice.other.charges')}
                    </Grid>
                    <Grid item xs={4} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            value={getOtherChargesDisplayValue()}
                            disabled={true}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography
                                            style={{width: '100%', padding: '0px', border: 'none', backgroundColor: 'transparent', color: 'gray', fontSize: '14px'}}
                                        >
                                            {getOtherChargesType() === 1 ? "$" : "%"}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={4} md={4} style={{justifyContent: "end", display: "flex"}}>
                        {otherChargesValue.toFixed(2)}
                    </Grid>
                </Grid>
                <hr/>
                <div className="mt-3">
                    {calculateTaxSummary()?.map((taxGroup: any) => (
                        <Grid key={`${taxGroup.id}-${taxGroup.rate}`} style={{ display: taxGroup.name ? "flex" : "none", alignItems: "center", marginBottom: "5px"}}>
                            <Grid md={6} className='justify-content-start'>
                                {`${taxGroup.name} [${taxGroup.rate > 0 ? `${taxGroup.rate}%` : `$${taxGroup.amount}`}]`}
                            </Grid>
                            <Grid md={3} style={{display: "flex", justifyContent: "middle"}}>
                                {Number(taxGroup.baseAmount).toFixed(2)}
                            </Grid>
                            <Grid md={3} style={{display: "flex", justifyContent: "flex-end"}}>
                                {Number(taxGroup.totalTax).toFixed(2)}
                            </Grid>
                        </Grid>
                    ))}
                </div>

                {input?.invoiceTaxes?.map((tax, index) => (
                    <Grid container md={12} key={tax.idTaxRate || index} style={{ display: "flex", alignItems: "center"}}>
                        <Grid md={6} className='justify-content-start'>
                            <Typography variant="body2">
                                {tax.description} [{tax.taxRate === 0 ? "$" : ""}{tax.taxRate > 0 ? tax.taxRate : tax.taxAmount}{tax.taxRate > 0 ? "%" : ""}]
                            </Typography>
                        </Grid>
                        <Grid md={6} style={{justifyContent: "end", display: "flex"}}>
                            {tax.taxRate > 0 ? Number(tax.taxRate * subTotalWithAddedValues / 100).toFixed(2) : Number(tax?.taxAmount || 0).toFixed(2)}
                        </Grid>
                    </Grid>
                ))}

                <Grid style={{marginTop: "15px", display: "flex", background: "#E7F4FF", padding: "10px", borderRadius : "5px"}}>
                    <Grid md={6} className='justify-content-start'>
                        <b>{intl.get('newInvoice.total')}</b>
                    </Grid>
                    <Grid md={6} style={{justifyContent: "end", display: "flex"}}>
                        <b>{Number(finalResult).toFixed(2)}</b>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default NewInvoiceSummaryView;
